import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useWarehouse } from './context/WarehouseProvider';
import vconst from 'utils/helpers/structs/vconst';
import { useAuth } from 'context/AuthContext';
import useSettingsParams from 'hooks/useSettingsParams';
import { canWarehouseOps, hasUserAccess } from 'utils/helpers/access/hasUserAccess';
import { getObjectCardPath } from 'utils/helpers/objects/objectCardPath';
import WarehouseTeamDropdown from './TeamManage/WarehouseTeamDropdown';
import ListToolBarActions from 'components/toolbar/ListToolBarActions';
import EToolTip from 'components/others/EToolTip';
import SolarIcon from 'components/icons/SolarIcon';

interface WarehouseToolBarProps {
    wId: number;
    setShowComing: (show: boolean | number) => void;
    setShowTransfer: (show: boolean | number | 'all') => void;
    setShowFilter: (show: boolean) => void;
    teamId?: number | null;
    teamName?: string;
    teamDeleted?: boolean;
    customId?: number | null;
    customName?: string;
    customDeleted?: boolean;
    warehouseEmpty?: boolean | null;
    warehouseIdApi?: string | null;
    onTeamChanged?: () => Promise<void>;
    reservObjectId?: number | null;
    reservOnlySale?: boolean | number | null;
}

const WarehouseToolBar = ({
    setShowComing,
    setShowTransfer,
    setShowFilter,
    wId,
    teamId,
    teamName,
    teamDeleted,
    customId,
    customName,
    customDeleted,
    warehouseEmpty,
    warehouseIdApi,
    onTeamChanged,
    reservObjectId,
    reservOnlySale,
}: WarehouseToolBarProps) => {
    const { t } = useTranslation(['Warehouses', 'common']);
    const navigate = useNavigate();
    const { stockFilter, showCredit, setShowCredit, triggerListReset, triggerListRefresh } = useWarehouse();
    const { auth } = useAuth();
    const params = useSettingsParams();
    const canOps = canWarehouseOps(auth?.user);
    const canTeamsEdit = hasUserAccess(auth?.user, 'teamsEdit');
    const showTeamItems = !!teamId && !teamDeleted;
    const showCustomItems = !!customId && !customDeleted;
    const showCreditToggle =
        Boolean(params.creditControl) &&
        auth?.user?.access?.role === 'manager' &&
        wId === vconst.waherouseMainId;
    const filterActive = Boolean(
        stockFilter &&
            ((Array.isArray(stockFilter.vendors) && stockFilter.vendors.length > 0) ||
                stockFilter.showAll ||
                Number(stockFilter.categoryId) > 0),
    );
    const isReservSale = Number(reservOnlySale) === 1 || reservOnlySale === true;

    return (
        <div className="mb-3 warehouse-toolbar">
            <div className="page-toolbar-actions warehouse-toolbar-actions">
                {!!reservObjectId && (
                    <EToolTip title={isReservSale ? t('toolShowSale') : t('toolShowObject')}>
                        <span className="d-inline-block ms-1">
                            <Button
                                className="fs-14 px-2 toolbar-icon-btn"
                                size="sm"
                                variant="success"
                                onClick={(e) => {
                                    e.currentTarget.blur();
                                    navigate(getObjectCardPath(Number(reservObjectId), isReservSale));
                                }}
                            >
                                <SolarIcon name={isReservSale ? 'wallet2' : 'brightness-high'} className="fs-14 p-0" />
                            </Button>
                        </span>
                    </EToolTip>
                )}

                {wId == vconst.waherouseMainId && canOps && (
                    <Button
                        className="fs-14 px-2 ms-1"
                        size="sm"
                        variant="success"
                        onClick={(e) => {
                            e.currentTarget.blur();
                            setShowComing(true);
                        }}
                    >
                        <SolarIcon name="plus" className="fs-14 p-0" />
                        <span className="d-none d-md-inline ms-1">{t('toolComming')}</span>
                    </Button>
                )}

                <EToolTip title={t('toolTransfer')}>
                    <span className="d-inline-block ms-1">
                        <Button
                            className="fs-14 px-2 toolbar-icon-btn"
                            size="sm"
                            variant="warning"
                            disabled={!canOps}
                            onClick={(e) => {
                                e.currentTarget.blur();
                                setShowTransfer(true);
                            }}
                        >
                            <SolarIcon name="transfer" className="fs-14 p-0" />
                        </Button>
                    </span>
                </EToolTip>

                {wId !== vconst.waherouseMainId && canOps && (
                    <EToolTip title={t('toolTransferAll')}>
                        <span className="d-inline-block ms-1">
                            <Button
                                className="fs-14 px-2 toolbar-icon-btn"
                                size="sm"
                                variant="warning"
                                disabled={warehouseEmpty === true}
                                onClick={(e) => {
                                    e.currentTarget.blur();
                                    setShowTransfer('all');
                                }}
                            >
                                <SolarIcon name="chevron-double-right" className="fs-14 p-0" />
                            </Button>
                        </span>
                    </EToolTip>
                )}

                {showCreditToggle && (
                    <EToolTip title={showCredit ? t('filter.labelHideCredit') : t('filter.labelShowCredit')}>
                        <span className="d-inline-block ms-1">
                            <Button
                                className="fs-14 px-2 toolbar-icon-btn"
                                size="sm"
                                variant={showCredit ? 'info' : 'secondary'}
                                onClick={(e) => {
                                    e.currentTarget.blur();
                                    setShowCredit((prev) => !prev);
                                }}
                            >
                                <SolarIcon name="cash-coin" className="fs-14 p-0" />
                            </Button>
                        </span>
                    </EToolTip>
                )}

                <WarehouseTeamDropdown
                    warehouseId={wId}
                    warehouseIdApi={warehouseIdApi}
                    showTeamItems={showTeamItems}
                    teamId={teamId}
                    teamName={teamName || ''}
                    showCustomItems={showCustomItems}
                    customId={customId}
                    customName={customName || ''}
                    warehouseEmpty={warehouseEmpty === true}
                    canManage={canTeamsEdit}
                    onChanged={onTeamChanged}
                />

                <ListToolBarActions onReset={triggerListReset} onRefresh={triggerListRefresh} />

                <EToolTip title={t('filter.title')}>
                    <span className="d-inline-block">
                        <Button
                            className="fs-14 px-2 ms-1 "
                            size="sm"
                            variant={filterActive ? 'danger' : 'primary'}
                            onClick={(e) => {
                                e.currentTarget.blur();
                                setShowFilter(true);
                            }}
                        >
                            <SolarIcon name="funnel" className="fs-14 p-0" />
                        </Button>
                    </span>
                </EToolTip>
            </div>
        </div>
    );
};

export default WarehouseToolBar;
